import React from 'react';
import { Card, CardContent, Box, Skeleton, Stack, Divider } from '@mui/material';

interface QuestionReportCardSkeletonProps {
  variant?: 'with-chart' | 'with-comments' | 'with-both' | 'minimal';
  chartSize?: 'small' | 'medium' | 'large';
  commentCount?: number;
  compact?: boolean;
  animate?: boolean;
  isMobile?: boolean;
}

export const QuestionReportCardSkeleton: React.FC<QuestionReportCardSkeletonProps> = ({
  variant = 'with-both',
  chartSize = 'medium',
  commentCount = 2,
  compact = false,
  animate = true,
  isMobile = false,
}) => {
  const getChartHeight = (): number => {
    switch (chartSize) {
      case 'small':
        return 250;
      case 'large':
        return 450;
      default:
        return 350;
    }
  };

  return (
    <Card
      sx={{
        mb: compact ? 2 : 3,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <CardContent sx={{ p: isMobile ? 2 : compact ? 2 : 3 }}>
        {/* Header Section */}
        <Box mb={isMobile ? 2 : 3}>
          {/* Chips Row */}
          <Box display="flex" gap={isMobile ? 0.5 : 1} mb={1} flexWrap="wrap">
            <Skeleton
              variant="rectangular"
              width={40}
              height={24}
              sx={{ borderRadius: 2 }}
              animation={animate ? 'wave' : false}
            />
            <Skeleton
              variant="rectangular"
              width={100}
              height={24}
              sx={{ borderRadius: 2 }}
              animation={animate ? 'wave' : false}
            />
          </Box>

          {/* Question Title (2 lines) */}
          <Skeleton
            variant="text"
            width={isMobile ? '95%' : '75%'}
            height={isMobile ? 28 : 32}
            sx={{ mb: 0.5 }}
            animation={animate ? 'wave' : false}
          />
          <Skeleton
            variant="text"
            width={isMobile ? '85%' : '60%'}
            height={isMobile ? 28 : 32}
            animation={animate ? 'wave' : false}
          />
        </Box>

        {/* Chart Section */}
        {(variant === 'with-chart' || variant === 'with-both') && (
          <Box mb={isMobile ? 2 : 3}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={getChartHeight()}
              sx={{ borderRadius: 1 }}
              animation={animate ? 'wave' : false}
            />
          </Box>
        )}

        {/* Comments Section */}
        {(variant === 'with-comments' || variant === 'with-both') && (
          <Box>
            <Divider sx={{ mb: 2 }} />

            {/* Comments Header */}
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Skeleton
                variant="circular"
                width={20}
                height={20}
                animation={animate ? 'wave' : false}
              />
              <Skeleton
                variant="text"
                width={150}
                height={24}
                animation={animate ? 'wave' : false}
              />
            </Box>

            {/* Comment Cards */}
            <Stack spacing={compact ? 1 : 2}>
              {Array.from({ length: commentCount }, (_, index) => (
                <Card
                  key={index}
                  sx={{
                    backgroundColor: 'transparent',
                    border: '2px solid',
                    borderColor: 'divider',
                  }}
                >
                  <CardContent sx={{ p: compact ? 1.5 : 2 }}>
                    <Box display="flex" justifyContent="space-between" gap={2}>
                      {/* Comment text */}
                      <Box flex={1}>
                        <Skeleton
                          variant="text"
                          width="100%"
                          height={16}
                          animation={animate ? 'wave' : false}
                        />
                        <Skeleton
                          variant="text"
                          width="85%"
                          height={16}
                          animation={animate ? 'wave' : false}
                        />
                      </Box>

                      {/* Timestamp and chip */}
                      <Box display="flex" alignItems="center" gap={1}>
                        <Skeleton
                          variant="rectangular"
                          width={70}
                          height={24}
                          sx={{ borderRadius: 2 }}
                          animation={animate ? 'wave' : false}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        )}

        {/* Minimal variant - no chart or comments */}
        {variant === 'minimal' && (
          <Box textAlign="center" py={4}>
            <Skeleton
              variant="circular"
              width={48}
              height={48}
              sx={{ mx: 'auto', mb: 2 }}
              animation={animate ? 'wave' : false}
            />
            <Skeleton
              variant="text"
              width={200}
              height={16}
              sx={{ mx: 'auto' }}
              animation={animate ? 'wave' : false}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
